import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { INTEREST_OPTIONS, CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildWhatsAppUrl = (f) => {
  const lines = [
    `Hi T Plus One, I'd like to enquire.`,
    ``,
    `Name: ${f.name}`,
    `Email: ${f.email}`,
    `Phone: ${f.phone}`,
    `Interested in: ${f.interest}`,
    `Preferred mode: ${f.mode}`,
  ];
  if (f.message?.trim()) lines.push(`Message: ${f.message.trim()}`);
  return `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
};

const inputCls =
  "w-full rounded-sm border border-line bg-white px-4 py-3.5 text-sm text-ink placeholder:text-tertiary transition-colors focus:border-pine focus:outline-none";

const EnquiryForm = ({ compact = false }) => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: params.get("interest") || INTEREST_OPTIONS[0],
    mode: "Online",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [waUrl, setWaUrl] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (form.phone.trim().length < 7) e.phone = "Please enter a valid phone number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/enquiries`, form);
      const url = buildWhatsAppUrl(form);
      setWaUrl(url);
      setStatus("success");
      toast.success("Enquiry received. The T Plus One team will reach out shortly.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="enquiry-success" className="rounded-sm border border-pine/30 bg-pine-light p-10 text-center">
        <p className="eyebrow text-pine">Received</p>
        <h3 className="mt-4 text-2xl tracking-tight">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-sm text-dim leading-relaxed max-w-sm mx-auto">
          Your enquiry about <span className="font-medium text-ink">{form.interest}</span> has been received. Continue the conversation instantly on WhatsApp — your details are already filled in.
        </p>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="enquiry-whatsapp-btn"
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-pine text-ivory px-8 py-4 text-sm font-medium transition-colors hover:bg-pine-2"
        >
          Continue on WhatsApp →
        </a>
        <div>
          <button
            data-testid="enquiry-again-btn"
            onClick={() => { setStatus("idle"); setForm({ ...form, name: "", email: "", phone: "", message: "" }); }}
            className="mt-6 text-sm font-medium text-dim hover:text-pine link-arrow inline-flex items-center gap-2 transition-colors"
          >
            Send another enquiry <span className="arrow">→</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="space-y-4" noValidate>
      <div className={`grid ${compact ? "" : "sm:grid-cols-2"} gap-4`}>
        <div>
          <label htmlFor="eq-name" className="eyebrow block mb-2">Full Name</label>
          <input id="eq-name" data-testid="enquiry-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" autoComplete="name" />
          {errors.name && <p className="mt-1.5 text-xs text-red-700" role="alert">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="eq-email" className="eyebrow block mb-2">Email</label>
          <input id="eq-email" data-testid="enquiry-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
          {errors.email && <p className="mt-1.5 text-xs text-red-700" role="alert">{errors.email}</p>}
        </div>
      </div>
      <div className={`grid ${compact ? "" : "sm:grid-cols-2"} gap-4`}>
        <div>
          <label htmlFor="eq-phone" className="eyebrow block mb-2">Phone</label>
          <input id="eq-phone" data-testid="enquiry-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 …" autoComplete="tel" />
          {errors.phone && <p className="mt-1.5 text-xs text-red-700" role="alert">{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor="eq-interest" className="eyebrow block mb-2">Interested In</label>
          <select id="eq-interest" data-testid="enquiry-interest" className={inputCls} value={form.interest} onChange={set("interest")}>
            {INTEREST_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      </div>
      <div>
        <span className="eyebrow block mb-2">Preferred Learning Mode</span>
        <div className="flex gap-2" role="radiogroup" aria-label="Preferred learning mode">
          {["Online", "Offline", "Either"].map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={form.mode === m}
              data-testid={`enquiry-mode-${m.toLowerCase()}`}
              onClick={() => setForm({ ...form, mode: m })}
              className={`rounded-full px-5 py-2.5 text-sm border transition-colors ${
                form.mode === m ? "bg-ink text-ivory border-ink" : "border-line text-dim hover:border-ink/30"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label htmlFor="eq-message" className="eyebrow block mb-2">Message <span className="normal-case tracking-normal">(optional)</span></label>
        <textarea id="eq-message" data-testid="enquiry-message" rows={compact ? 3 : 4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Anything you'd like us to know…" />
      </div>
      <button
        type="submit"
        data-testid="enquiry-submit-btn"
        disabled={status === "loading"}
        className="w-full rounded-full bg-ink text-ivory py-4 text-sm font-medium transition-colors hover:bg-pine disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        {status === "loading" && <Loader2 size={16} className="animate-spin" />}
        {status === "loading" ? "Sending…" : "Submit Enquiry"}
      </button>
    </form>
  );
};

export default EnquiryForm;
